import React from 'react';
import { formatMoney } from '../../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import type { Product, ProductSize, PosOptionGroup, SelectedOptionEntry } from '../../types/order';
import { lineUnitPrice } from '../../utils/pricing';

interface ProductOptionsModalProps {
  product: Product;
  /** Size already chosen upstream (from size modal). Null for single-size or no-size products. */
  pendingSize: ProductSize | null;
  /** Option groups to display. In quick-click flow, usually only required groups. */
  groups: PosOptionGroup[];
  /** Currently selected option entries. */
  selectedOptions: SelectedOptionEntry[];
  /** Called when selection changes. */
  onChange: (options: SelectedOptionEntry[]) => void;
  /** Called when user presses Add to cart — parent must validate required groups. */
  onConfirm: () => void;
  /** Called when user cancels / closes the modal. */
  onClose: () => void;
  /** Order channel — picks the per-size price override (size-only stores). */
  channel?: 'take_away' | 'delivery' | 'dine_in';
  /** i18n namespace for button/label text (takeaway | delivery | dineIn). */
  translationNamespace?: string;
}

const ProductOptionsModal: React.FC<ProductOptionsModalProps> = ({
  product,
  pendingSize,
  groups,
  selectedOptions,
  onChange,
  onConfirm,
  onClose,
  channel = 'take_away',
  translationNamespace = 'takeaway',
}) => {
  const { t } = useTranslation([translationNamespace, 'common']);

  const toggleChoice = (group: PosOptionGroup, choice: PosOptionGroup['choices'][number]) => {
    const isSelected = selectedOptions.some(o => o.group_id === group.id && o.choice_id === choice.id);
    const entry: SelectedOptionEntry = {
      group_id: group.id,
      group_name: group.name,
      choice_id: choice.id,
      choice_name: choice.name,
      price_modifier: choice.price_modifier,
    };
    if (group.selection_type === 'single') {
      onChange([...selectedOptions.filter(o => o.group_id !== group.id), entry]);
    } else {
      onChange(
        isSelected
          ? selectedOptions.filter(o => !(o.group_id === group.id && o.choice_id === choice.id))
          : [...selectedOptions, entry]
      );
    }
  };

  const headerPrice = pendingSize ? lineUnitPrice(product, pendingSize, channel) : null;

  return (
    <div
      className="cashier-overlay fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
    >
      <div className="cashier-dialog bg-surface-1 rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="cashier-dialog-header p-5 border-b border-line">
          <h2 className="text-xl font-bold text-t1">{product.name}</h2>
          {pendingSize && headerPrice !== null && (
            <p className="text-blue-600 dark:text-blue-400 text-sm mt-0.5">
              {pendingSize.name} · {formatMoney(headerPrice)}
            </p>
          )}
        </div>

        {/* Groups */}
        <div
          className="p-5 space-y-5 overflow-y-auto flex-1 overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {groups.map(group => {
            const choiceCount = group.choices.length;
            const choiceGridCols =
              choiceCount <= 1 ? 'grid-cols-1' :
              choiceCount === 2 ? 'grid-cols-2' :
              choiceCount === 3 ? 'grid-cols-3' :
              choiceCount === 4 ? 'grid-cols-2 sm:grid-cols-4' :
              'grid-cols-3 sm:grid-cols-4 lg:grid-cols-5';
            return (
            <div key={group.id}>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-base font-bold text-t1">{group.name}</h3>
                {group.is_required && (
                  <span className="text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full font-semibold">
                    {t(`${translationNamespace}:required`)}
                  </span>
                )}
                {group.selection_type === 'multiple' && (
                  <span className="text-xs text-t4">
                    {t(`${translationNamespace}:multiple`)}
                  </span>
                )}
              </div>
              <div className={`grid ${choiceGridCols} gap-3`}>
                {group.choices.map(choice => {
                  const isSelected = selectedOptions.some(
                    o => o.group_id === group.id && o.choice_id === choice.id
                  );
                  return (
                    <button
                      key={choice.id}
                      onClick={() => toggleChoice(group, choice)}
                      className={`p-4 rounded-xl border-2 transition-all active:scale-[0.97] min-h-[96px] flex flex-col items-center justify-center text-center ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                          : 'border-line-strong hover:border-line-strong hover:bg-surface-2'
                      }`}
                    >
                      <span className="font-bold text-lg text-t1">{choice.name}</span>
                      {choice.price_modifier > 0 && (
                        <span className="text-sm mt-1 font-bold text-blue-600 dark:text-blue-400">
                          +{formatMoney(choice.price_modifier)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-line flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-4 rounded-xl border-2 border-line-strong text-t2 font-bold text-base hover:bg-surface-2 active:scale-95 transition-all"
          >
            {t(`${translationNamespace}:cancel`)}
          </button>
          <button
            onClick={onConfirm}
            className="cashier-primary flex-[2] py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-base active:scale-95 transition-all"
          >
            {t(`${translationNamespace}:addToCart`)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductOptionsModal;
